import React, { useState } from 'react';
import { 
  Globe,
  Eye, 
  EyeOff,
  ArrowRight,
  Building2
} from 'lucide-react';
import styles from './PasswordReset.module.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL } from '../../Components/Api/Api';
import Swal from 'sweetalert2';
import { BRANDING } from '../../Components/Branding/Branding';
import { GoShieldCheck } from 'react-icons/go';
import { RiPieChart2Fill } from 'react-icons/ri';
import { HiMiniUsers } from 'react-icons/hi2';
import { FaRegUser } from 'react-icons/fa';
import { LuLockKeyhole } from 'react-icons/lu';

export default function PasswordReset() {
  // Form States
  const [identifier, setIdentifier] = useState(''); // Handles Membership Number or Email
  const [password, setPassword] = useState('');
  const [password1, setPassword1] = useState('');

  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCompanySelection, setShowCompanySelection] = useState(false);
  const [loginData, setLoginData] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [emailError, setEmailError] = useState(''); // Direct red error text under input
  const [companies, setCompanies] = useState([]); // Stores fetched companies
  const [companiesLoading, setCompaniesLoading] = useState(false); // Mid-screen loader trigger
  
  // UI States
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [language, setLanguage] = useState('English');
  
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  // Helper to fetch companies only after a successful login trigger
  const fetchCompanies = async (emailToFetch) => {
    setCompaniesLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/my-active-coop?email=${emailToFetch}`);
      const companyList = response.data?.companies || response.data?.data || [];
      
      setCompanies(companyList);

      if (companyList.length > 0) {
        setEmailError('');
        setSelectedCompanyId(companyList[0]?.id || '');
        return true; // Signifies successful retrieval
      } else {
        setSelectedCompanyId('');
        setEmailError(response.data?.message || 'No company found.');
        return false;
      }
    } catch (error) {
      let errorMessage = 'An error occurred fetching companies. Please try again.';
      if (error.response && error.response.data && error.response.data.message) {
        const { message } = error.response.data;
        if (typeof message === 'string') errorMessage = message;
        else if (Array.isArray(message)) errorMessage = message.join('; ');
        else if (typeof message === 'object') {
          const firstKey = Object.keys(message)[0];
          const messages = message[firstKey];
          errorMessage = Array.isArray(messages) ? messages.join('; ') : messages;
        }
      }
      setEmailError(errorMessage);
      setCompanies([]);
      setSelectedCompanyId('');
      return false;
    } finally {
      setCompaniesLoading(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/member-auth/login`, {
        email: identifier, 
        password: password,
      });
      
      const apiData = response.data?.data || {};
      setLoginData(apiData);
      
      // Save general user details first
      localStorage.setItem('token', apiData?.token);
      localStorage.setItem('memberNumber', apiData?.member?.member_number);
      localStorage.setItem('memberPhoneNumber', apiData?.member?.phone_number);
      localStorage.setItem('department', apiData?.member?.department);
      localStorage.setItem('employerName', apiData?.member?.employer_name);
      localStorage.setItem('saveType', apiData?.member?.savings_type);
      localStorage.setItem('memberName', apiData?.member?.name);
      localStorage.setItem('memberAddress', apiData?.member?.address);
      localStorage.setItem('memberPhoto', apiData?.member?.passport_photograph);
      localStorage.setItem('walletBalance', apiData?.member?.wallet_balance);
      
      // Fetch active cooperatives on successful login verification
      const companiesFetched = await fetchCompanies(identifier);

      if (companiesFetched) {
        // Show company selection layout
        setShowCompanySelection(true);
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'Account Restricted',
          text: 'Login successful, but we could not find any active companies registered to this account.',
        });
      }
    } catch (error) {
      let errorMessage = 'An error occurred. Please try again.';
      if (error.response && error.response.data && error.response.data.message) {
        const { message } = error.response.data;
        if (typeof message === 'string') errorMessage = message;
        else if (Array.isArray(message)) errorMessage = message.join('; ');
        else if (typeof message === 'object') {
          const firstKey = Object.keys(message)[0];
          const messages = message[firstKey];
          errorMessage = Array.isArray(messages) ? messages.join('; ') : messages;
        }
      }
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySubmit = (e) => {
    e.preventDefault();
    if (!selectedCompanyId) {
      Swal.fire({
        icon: 'warning',
        title: 'Selection Required',
        text: 'Please select a company to proceed.',
      });
      return;
    }

    const activeCompanies = companies.length > 0 ? companies : (loginData?.companies || loginData?.member?.companies || []);
    const selectedCompany = activeCompanies.find(c => String(c.id) === String(selectedCompanyId));

    if (selectedCompany) {
      localStorage.setItem('companyId', selectedCompany.id);
      localStorage.setItem('companyName', selectedCompany.name || selectedCompany.company_name);
      navigate('/dashboard'); 
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Invalid company selection.',
      });
    }
  };

  return (
    <div className={styles.screenWrapper}>
      {companiesLoading && (
        <div className={styles.globalLoaderOverlay}>
          <div className={styles.spinnerContainer}>
            <div className={styles.spinner}></div>
            <p className={styles.loadingText}>Fetching registered companies...</p>
          </div>
        </div>
      )}
      
      {/* LEFT COLUMN: BRAND SIDEBAR */}
      <div className={styles.leftSidebar}>
        <div className={styles.sidebarOverlay}></div>
        <div className={styles.sidebarContentWrapper}>
          <div className={styles.brandTopArea}>
            <div className={styles.logoContainer}>
              <img src={BRANDING?.coopPrimary?.logo} alt="RCCG Logo" className={styles.logoImg} />
            </div>
          </div>
          
          <div className={styles.sidebarMidContent}>
            <h1>Stronger Together,<br />Better Together</h1>
            <p>Empowering members to save, borrow, <br /> invest and grow financially.</p>
            <div className={styles.sideBarBottom} />
            <ul className={styles.featuresList}>
              <li>
                <span className={styles.featureIcon}><GoShieldCheck size={25} /></span>
                <div>
                  <strong>Secure & Trusted</strong>
                  <p>Your data is protected with enterprise-grade security</p>
                </div>
              </li>
              <li>
                <span className={styles.featureIcon}><RiPieChart2Fill size={25}/></span>
                <div>
                  <strong>Smart & Transparent</strong>
                  <p>Track your savings, loans and investments in real time</p>
                </div>
              </li>
              <li>
                <span className={styles.featureIcon}><HiMiniUsers size={25}/></span>
                <div>
                  <strong>Member Focused</strong>
                  <p>Built to support your financial wellbeing and future</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* RIGHT COLUMN: INTERACTIVE PORTAL */}
      <div className={styles.rightContentArea}>
        <div className={styles.portalActionCard}>
          
              <div className={styles.cardHeaderArea}>
                <h3>Password Reset</h3>
                <p>Enter your password</p>
              </div>

              <form 
              // onSubmit={handleLoginSubmit} 
              className={styles.formSpace}>
                {/* Field 1: Email */}
                {/* <div className={styles.cleanInputGroup}>
                  <label>Email Address</label>
                  <div className={styles.fieldInteractiveWrapper}>
                    <span className={styles.inputPrefixIcon}><FaRegUser size={18} color='#000'/></span>
                    <input 
                      type="text" 
                      required 
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      placeholder="Enter your email address" 
                      className={styles.cleanInput} 
                    />
                  </div>
                  {emailError && <span className={styles.errorText} style={{ color: 'red', fontSize: '12px' }}>{emailError}</span>}
                </div> */}

                {/* Field 2: Password */}
                <div className={styles.cleanInputGroup}>
                  <label>Enter New Password</label>
                  <div className={styles.fieldInteractiveWrapper}>
                    <span className={styles.inputPrefixIcon}><LuLockKeyhole size={18} color='#000'/></span>
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter new password"
                      className={styles.cleanInput}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className={styles.endFieldIcon}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                 <div className={styles.cleanInputGroup}>
                  <label>Confirm Password</label>
                  <div className={styles.fieldInteractiveWrapper}>
                    <span className={styles.inputPrefixIcon}><LuLockKeyhole size={18} color='#000'/></span>
                    <input
                      type={showPassword1 ? "text" : "password"}
                      required
                      value={password1}
                      onChange={(e) => setPassword1(e.target.value)}
                      placeholder="Enter your password"
                      className={styles.cleanInput}
                    />
                    <button type="button" onClick={() => setShowPassword1(!showPassword1)} className={styles.endFieldIcon}>
                      {showPassword1 ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Remember Me & Forgot Password */}
                {/* <div className={styles.formOptionsRow}> */}
                  {/* <label className={styles.rememberMeLabel}>
                    <input 
                      type="checkbox" 
                      checked={rememberMe} 
                      onChange={(e) => setRememberMe(e.target.checked)} 
                    />
                    <span>Remember me</span>
                  </label> */}
                  {/* <span className={styles.forgotPasswordLink}>Forgot Password?</span>
                </div> */}

                {/* Sign In Button */}
                <button type="submit" className={styles.submitActionBtn} disabled={loading}>
                  <span>{loading ? "Processing..." : "Proceed"}</span>
                  {!loading && <ArrowRight size={18} />}
                </button>
              </form>

              {/* <div className={styles.supportCenterFooter}>
                Don't have portal access? <span style={{color: '#16A34A', cursor: 'pointer'}} onClick={() => navigate('/member-registration')}>Click here!</span>
              </div> */}
        
        </div>
      </div>

    </div>
  );
}